import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    BadgeDollarSign,
    CircleAlert,
    CircleSlash,
    FileQuestion,
    Home,
    NotebookText,
    Vote,
    MessageCircle,
} from "lucide-react";
import useSound from "../../Services/SoundService";
import TelegramIcon from "../../assets/icons/telegram.svg";
import DiscordIcon from "../../assets/icons/discord.svg";
import { useTranslation } from "react-i18next";

// Add X (Twitter) icon component
const XIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
    </svg>
);

const AppFooter = () => {
    const { t } = useTranslation();

    const links = [
        { href: "/", icon: Home, name: t("links.home") },
        { href: "/how-bidcoin-auctions-work", icon: FileQuestion, name: t("links.howItWorks") },
        { href: "/auctions", icon: BadgeDollarSign, name: t("links.auctions") },
        { href: "/staking", icon: CircleSlash, name: t("links.staking") },
        { href: "/cw", icon: MessageCircle, name: t("links.communityHub") },
    ];

    const whitepaper = {
        href: "https://whitepaper.bidcoin.live",
        icon: NotebookText,
        name: t("whitepaper.name"),
    };

    const supply = {
        href: "https://supply.bidcoin.live",
        icon: CircleSlash,
        name: t("supply.name"),
    };

    const navigate = useNavigate();
    const { play: playNavigationSound, SoundControl } = useSound(
        "/sounds/tab_change.wav",
        "public"
    );

    const handleNavigation = (path) => {
        playNavigationSound();
        window.scrollTo({ behavior: "smooth", top: 0 });
        navigate(path);
    };

    return (
        <footer className="">
            <div className="max-w-7xl mx-auto px-4 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                    {/* Brand Section */}
                    <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                            {/* <img src="/favicon.ico" alt="Logo" className="h-8 w-8" /> */}
                            <span className="text-amber-500 font-medium">{t("brand.name")}</span>
                        </div>
                        <p className="text-zinc-400 text-sm">{t("brand.description")}</p>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-4">
                        <h3 className="text-zinc-100 font-semibold">{t("quickLinks.title")}</h3>
                        <div className="flex flex-col space-y-2">
                            {links.map((link) => (
                                <button
                                    key={link.href}
                                    onClick={() => handleNavigation(link.href)}
                                    className="text-left text-zinc-400 hover:text-white text-sm transition-colors"
                                >
                                    {link.name}
                                </button>
                            ))}
                            <a
                                href={whitepaper.href}
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {whitepaper.name}
                            </a>
                            <a
                                href={supply.href}
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {supply.name}
                            </a>
                        </div>
                    </div>

                    {/* Community */}
                    <div className="space-y-4">
                        <h3 className="text-zinc-100 font-semibold">{t("community.title")}</h3>
                        <div className="flex flex-col space-y-2">
                            <a
                                href="https://discord.gg/dnh4y8zSgd"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white text-sm transition-colors flex items-center space-x-2"
                            >
                                <img
                                    src={DiscordIcon}
                                    alt="Discord"
                                    className="h-4 w-4 opacity-50"
                                />
                                <span>{t("community.discord")}</span>
                            </a>
                            <a
                                href="https://x.com/bidcoinnews"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white text-sm transition-colors flex items-center space-x-2"
                            >
                                <XIcon />
                                <span>{t("community.twitter")}</span>
                            </a>
                            <a
                                href="https://t.me/BidCoinNews"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white text-sm transition-colors flex items-center space-x-2"
                            >
                                <img src={TelegramIcon} alt="Telegram" className="h-4 w-4" />
                                <span>{t("community.telegram")}</span>
                            </a>
                            <a
                                href="https://medium.com/@BidCoinNews"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white text-sm transition-colors flex items-center space-x-2"
                            >
                                <MessageCircle className="h-4 w-4" />
                                <span>{t("community.medium")}</span>
                            </a>
                        </div>
                    </div>

                    {/* Legal */}
                    <div className="space-y-4">
                        <h3 className="text-zinc-100 font-semibold">{t("legal.title")}</h3>
                        <div className="flex flex-col space-y-2">
                            <Link
                                to="/term-and-conditions"
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {t("legal.terms")}
                            </Link>
                            <Link
                                to="/privacy"
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {t("legal.privacy")}
                            </Link>
                            <Link
                                to="/legal-refund"
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {t("legal.refund")}
                            </Link>
                            <Link
                                to="/cookie-policy"
                                className="text-zinc-400 hover:text-white text-sm transition-colors"
                            >
                                {t("legal.cookie")}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="mt-2 pt-2 border-t border-zinc-800/50 text-center">
                    <p className="text-zinc-400 text-sm">{t("copyright")}</p>
                </div>
            </div>
        </footer>
    );
};

export default AppFooter;
